import type { CheckRunDetail } from './queries.js';
import { actorLabel } from './people.js';
import { assetTypeLabel } from './asset-types.js';

/** One row per item, with the check's details repeated so the file stands alone in a spreadsheet. */
export function checkRunCsv(run: CheckRunDetail): string {
  const header = [
    'Asset', 'Asset type', 'Team', 'Check type', 'Date', 'Check status', 'Submitted by', 'Submitted at',
    'Section', 'Equipment', 'Serial', 'Expected', 'Status', 'Reading', 'Flagged', 'Note', 'External ref',
    'Checked by', 'Checked at', 'Photos',
  ];
  const common = [
    run.assetName, assetTypeLabel(run.assetType), run.teamName ?? 'Any crew',
    run.scheduleId ? 'Scheduled' : 'On demand', run.periodStart.slice(0, 10), run.status,
    run.submittedByName ? actorLabel(run.submittedByName, run.submittedByActive) : '',
    run.submittedAt ?? '',
  ];
  const rows = run.items.map((i) => [
    ...common,
    i.sectionName ?? '', i.equipmentName ?? '', i.serialNumber ?? '', i.expected ?? '',
    i.status ?? 'not checked', i.reading ?? '', i.flagged ? 'yes' : '', i.note ?? '', i.externalRef ?? '',
    i.checkedByName ? actorLabel(i.checkedByName, i.checkedByActive) : '', i.checkedAt ?? '',
    String(i.photoCount),
  ]);
  // CRLF + BOM so Excel opens it as UTF-8 with the right line breaks.
  return '﻿' + [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

function csvCell(v: string): string {
  // Quote when needed; neutralise leading =,+,-,@ so a note can't run as a spreadsheet formula.
  const safe = /^[=+\-@]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** e.g. "GR1 check 2026-09-20" → "GR1-check-2026-09-20.csv" */
export function reportFilename(run: CheckRunDetail, ext: 'csv' | 'pdf'): string {
  const base = `${run.assetName} check ${run.periodStart.slice(0, 10)}`.replace(/[^\w.-]+/g, '-');
  return `${base}.${ext}`;
}

export function downloadText(filename: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
